import React, { useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Password field with a show/hide toggle (ST-00025).
 *
 * Drop-in for `<input type="password" …>`: every prop except the ones below is
 * passed to the input, so className/style/autoComplete/required keep working.
 * The toggle sits inside the field's right edge; the input gets matching right
 * padding so long passwords never run under the icon.
 *
 * @param {'light'|'dark'} tone  icon colours: 'light' for app forms, 'dark' for
 *                               the glass auth screens (Login / Set password)
 * @param {string} wrapperClassName  classes on the positioning wrapper (e.g. mt-1)
 */
const PasswordInput = ({
  tone = 'light',
  wrapperClassName = '',
  style,
  id,
  disabled,
  ...inputProps
}) => {
  const [visible, setVisible] = useState(false);
  const generatedId = useId();
  const inputId = id || generatedId;

  const toneClasses =
    tone === 'dark'
      ? 'text-white/50 hover:text-white focus-visible:ring-white/60'
      : 'text-slate-400 hover:text-slate-600 focus-visible:ring-primary-500';

  return (
    <div className={`relative ${wrapperClassName}`}>
      <input
        {...inputProps}
        id={inputId}
        type={visible ? 'text' : 'password'}
        disabled={disabled}
        // Browser-native reveal buttons (Edge) would sit on top of ours.
        className={`password-input ${inputProps.className || ''} !pr-11`}
        style={style ? { ...style, paddingRight: '2.75rem' } : undefined}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        aria-controls={inputId}
        title={visible ? 'Hide password' : 'Show password'}
        className={`absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset disabled:cursor-default disabled:opacity-40 ${toneClasses}`}
      >
        {visible ? (
          <EyeOff className="h-[18px] w-[18px]" aria-hidden="true" />
        ) : (
          <Eye className="h-[18px] w-[18px]" aria-hidden="true" />
        )}
      </button>
    </div>
  );
};

export default PasswordInput;
